import React, { Fragment, useState } from 'react';
import Swal from 'sweetalert2';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import ListaAdmin from '../../Components/ListaAdmin';
import FilaAdmin, { Insignia } from '../../Components/FilaAdmin';
import Paginacion from '../../Components/Paginacion';
import PanelEdicion from '../../Components/PanelEdicion';
import SeccionPanel from '../../Components/SeccionPanel';

import useListaPaginada from '../../Hooks/useListaPaginada';
import usePanelEdicion from '../../Hooks/usePanelEdicion';

import MensajesHelper from '../../Helpers/Mensajes';
import { enlaceWhatsapp, formatearFecha, formatearFechaHora } from '../../Helpers/solicitudes';

const estiloDato = { display: "flex", gap: "10px", margin: "0 0 8px", fontSize: "14px" };
const estiloEtiqueta = { width: "120px", flexShrink: 0, color: "#757575" };

const iniciales = nombre => String(nombre ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map(palabra => palabra[0].toUpperCase()).join('');

// `empresa` trae relleno (" ", "-", ".") cuando no se llenó; solo se muestra si tiene letras
const tieneTexto = valor => /[a-záéíóúñ0-9]/i.test(String(valor ?? ''));

const resumen = texto => {
    const limpio = String(texto ?? '').replace(/\s+/g, ' ').trim();
    return limpio.length > 90 ? limpio.slice(0, 90) + '…' : limpio;
};

const Dato = ({ etiqueta, children }) => (
    <p style={estiloDato}>
        <span style={estiloEtiqueta}>{etiqueta}</span>
        <span style={{ overflowWrap: "anywhere" }}>{children}</span>
    </p>
);

const Mensajes = () => {
    const [mensaje, setMensaje] = useState(null);
    const [eliminando, setEliminando] = useState(false);

    const lista = useListaPaginada({ obtener: MensajesHelper.listar });
    const panel = usePanelEdicion({ onCerrar: () => setMensaje(null) });

    const abrir = registro => {
        setMensaje(registro);
        panel.abrir('mensaje');
    }

    const eliminar = async () => {
        const { isConfirmed } = await Swal.fire({
            icon: 'question',
            titleText: '¿Eliminar el mensaje de ' + mensaje.nombre + '?',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!isConfirmed) return;
        setEliminando(true);
        const res = await MensajesHelper.eliminarMensaje(mensaje._id);
        setEliminando(false);
        if (!res?.success) return;
        panel.cerrar({ forzar: true });
        lista.recargar();
    }

    const whatsapp = mensaje && enlaceWhatsapp(mensaje.celular);
    const asunto = encodeURIComponent('Re: tu mensaje en CAAV');

    return (
        <Fragment>
            <SideNav />
            {eliminando && (
                <div style={{width:"100%", height:"100vh", position:"fixed", top:"0", background:"#00000059", zIndex:"9999"}}>
                    <Loader />
                </div>
            )}
            <div className='row'>
                <PanelEdicion
                    abierto={Boolean(panel.abierto)}
                    titulo='Mensaje de contacto'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    pie={mensaje && (
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_peligro' onClick={eliminar}>
                                Eliminar
                            </button>
                            {whatsapp && (
                                <a className='boton_admin boton_admin_secundario' href={whatsapp} target='_blank' rel='noopener noreferrer'>
                                    WhatsApp
                                </a>
                            )}
                            {mensaje.email && (
                                <a className='boton_admin' href={`mailto:${mensaje.email}?subject=${asunto}`}>
                                    Responder por correo
                                </a>
                            )}
                        </Fragment>
                    )}
                >
                    {mensaje && (
                        <Fragment>
                            <SeccionPanel id='de' titulo={mensaje.nombre} ubicacion={'Recibido el ' + formatearFechaHora(mensaje.createdAt)}>
                                <Dato etiqueta='Correo'>{mensaje.email ? <a href={'mailto:' + mensaje.email}>{mensaje.email}</a> : '—'}</Dato>
                                <Dato etiqueta='Celular'>{mensaje.celular ? <a href={'tel:' + mensaje.celular}>{mensaje.celular}</a> : '—'}</Dato>
                                <Dato etiqueta='Prefiere'>{mensaje.pago || '—'}</Dato>
                                {tieneTexto(mensaje.empresa) && <Dato etiqueta='Empresa'>{mensaje.empresa.trim()}</Dato>}
                                <Dato etiqueta='Desde'>{mensaje.vista ? 'Página de ' + mensaje.vista : '—'}</Dato>
                            </SeccionPanel>
                            <SeccionPanel id='mensaje' titulo='Mensaje'>
                                <p className='mensaje_admin_texto'>{mensaje.mensaje}</p>
                            </SeccionPanel>
                        </Fragment>
                    )}
                </PanelEdicion>
                <ListaAdmin
                    etiqueta='Contacto'
                    encabezado='Mensajes de contacto'
                    ayuda='Mensajes que llegan desde el formulario de contacto de los cursos. Haz clic en uno para leerlo y responder.'
                    busqueda={lista.busqueda}
                    onBusqueda={lista.setBusqueda}
                    placeholder='Buscar por nombre, correo, celular o texto del mensaje'
                    conteo={`${lista.total.toLocaleString('es-MX')} ${lista.total === 1 ? 'mensaje' : 'mensajes'}`}
                    mostrados={lista.registros.length}
                    cargando={lista.cargando}
                    actualizando={lista.actualizando}
                    textoVacio='Aún no hay mensajes.'
                    margen={panel.abierto ? panel.ancho : 0}
                    pie={lista.total > 0 && (
                        <Paginacion pagina={lista.pagina} limite={lista.limite} total={lista.total} onPagina={lista.setPagina} onLimite={lista.setLimite} />
                    )}
                >
                    {lista.registros.map(registro => (
                        <FilaAdmin
                            key={registro._id}
                            activa={mensaje?._id === registro._id}
                            onClick={() => abrir(registro)}
                            iniciales={iniciales(registro.nombre)}
                            titulo={registro.nombre}
                            subtitulo={resumen(registro.mensaje)}
                            insignias={
                                <Fragment>
                                    {registro.pago && <Insignia variante='gris'>{registro.pago}</Insignia>}
                                    <Insignia variante='gris'>{formatearFecha(registro.createdAt)}</Insignia>
                                </Fragment>
                            }
                        />
                    ))}
                </ListaAdmin>
            </div>
        </Fragment>
    );
};

export default Mensajes;
